import type { TranslationKey } from './en';

/**
 * Malayalam strings.
 *
 * Typed as `Record<TranslationKey, string>`, so a key added to `en.ts` and not
 * here fails `tsc`. That is the mechanism behind the owner's standing rule that
 * anything a parent comes across must be translated.
 *
 * Vocabulary decision (owner's, explicit): the academy's own terms are
 * TRANSLITERATED into Malayalam script, not replaced with Malayalam
 * descriptions — ഹിഫ്‌സ് rather than "മനഃപാഠ കോഴ്‌സ്". These are the words
 * Kerala Muslim families already use; describing them would read as alien to
 * exactly the audience the translation is for.
 *
 * Register: parents are addressed with the respectful plural (നിങ്ങൾ), and the
 * greeting keeps the Arabic salaam families actually use.
 *
 * Anything here is a draft pending the owner's review. Flag a line and it can
 * be replaced without touching code — this file is data.
 */
export const ml: Record<TranslationKey, string> = {
  // ── Shell chrome ──────────────────────────────────────────────────────────
  'nav.skipToContent': 'പ്രധാന ഉള്ളടക്കത്തിലേക്ക് പോകുക',
  'nav.primary': 'പ്രധാന മെനു',
  // The NEW badge — for the owner's review, like the rest of the Malayalam.
  'nav.new': 'പുതിയത്',
  'nav.openMenu': 'മെനു തുറക്കുക',
  'nav.closeMenu': 'മെനു അടയ്ക്കുക',
  'nav.signOut': 'പുറത്തുകടക്കുക',

  // ── Parent navigation ─────────────────────────────────────────────────────
  'nav.family': 'കുടുംബം',
  'nav.myChildren': 'എന്റെ കുട്ടികൾ',
  'nav.myFees': 'എന്റെ ഫീസ്',
  'nav.myAttendance': 'എന്റെ ഹാജർ',
  'nav.announcements': 'അറിയിപ്പുകൾ',
  'nav.myMarks': 'എന്റെ മാർക്ക്',
  'nav.teacherFeedback': 'അധ്യാപകനെക്കുറിച്ചുള്ള അഭിപ്രായം',
  'nav.referralsWallet': 'റഫറലുകളും വാലറ്റും',

  // ── Family landing ────────────────────────────────────────────────────────
  'family.title': 'കുടുംബം',
  'family.contactOffice': 'അക്കാദമി ഓഫീസുമായി ബന്ധപ്പെടുക.',
  'family.intro': 'നിങ്ങളുടെ കുട്ടികൾ, അടയ്‌ക്കേണ്ട ഫീസ്, അക്കാദമിയിൽ നിന്നുള്ള പുതിയ വിവരങ്ങൾ.',
  'family.kpi.children': 'കുട്ടികൾ',
  'family.kpi.noChildrenLinked': 'ഇതുവരെ കുട്ടികളെ ചേർത്തിട്ടില്ല',
  'family.kpi.feeDue': 'അടയ്‌ക്കേണ്ട ഫീസ്',
  'family.kpi.dueOn': 'അവസാന തീയതി {date}',
  'family.kpi.nothingOutstanding': 'ബാക്കി ഒന്നുമില്ല',
  'family.kpi.pendingFeedback': 'ബാക്കിയുള്ള അഭിപ്രായങ്ങൾ',
  'family.kpi.feedbackDisabled': 'അഭിപ്രായ സൗകര്യം ഇനിയും ലഭ്യമല്ല',
  'family.kpi.caughtUp': 'എല്ലാം പൂർത്തിയായി',
  'family.kpi.openBelow': 'താഴെയുള്ള പുതിയ ഭാഗത്തിൽ നിന്ന് തുറക്കുക',
  'family.kpi.wallet': 'വാലറ്റ്',
  'family.kpi.walletDisabled': 'വാലറ്റ് ഇനിയും ലഭ്യമല്ല',
  'family.kpi.acrossAccounts_one': '{count} അക്കൗണ്ടിൽ',
  'family.kpi.acrossAccounts_other': '{count} അക്കൗണ്ടുകളിൽ',
  'family.noRecentRemarks': 'സമീപകാല കുറിപ്പുകളില്ല',
  'family.noChildrenLinkedTitle': 'കുട്ടികളെ ചേർത്തിട്ടില്ല',
  'family.remarkFor': 'ആർക്ക്',
  'family.lastRemark': 'അവസാന കുറിപ്പ്:',
  'family.attendanceThisMonth': 'ഈ മാസത്തെ ഹാജർ:',
  'family.needHelp': 'ഹാജർ രേഖ തിരുത്തണോ, ഫീസിനെക്കുറിച്ച് ചോദിക്കണോ?',
  'family.openAnnouncements': 'അറിയിപ്പുകൾ തുറക്കുക',
  'family.forContactDetails': 'എന്നതിൽ അക്കാദമിയുടെ ബന്ധപ്പെടാനുള്ള വിവരങ്ങളുണ്ട്.',
  'family.addFirstChild': 'നിങ്ങളുടെ ആദ്യ കുട്ടിയെ ചേർക്കുക',
  'family.recentMarks': 'സമീപകാല മാർക്ക്',
  'family.noMarksYet': 'ഇതുവരെ മാർക്കില്ല',
  'family.marksWillAppear': 'അധ്യാപകർ ഫലം പ്രസിദ്ധീകരിച്ചാൽ പരീക്ഷാഫലം ഇവിടെ കാണാം.',
  'family.examsDisabled': 'പരീക്ഷാ സൗകര്യം ലഭ്യമാകുമ്പോൾ ഫലവും പ്രോഗ്രസും ഇവിടെ കാണാം.',
  'family.referralCodes': 'റഫറൽ കോഡുകൾ',
  'family.noCodesYet': 'ഇതുവരെ കോഡുകളില്ല',
  'family.codesWillAppear': 'നിങ്ങളുടെ കുട്ടി ചേർന്നാൽ പങ്കുവയ്ക്കാവുന്ന റഫറൽ കോഡ് ഇവിടെ കാണാം.',
  'family.referralsDisabled':
    'കുട്ടിയുടെ കോഡ് പങ്കുവച്ച് വാലറ്റ് ക്രെഡിറ്റ് നേടാം — റഫറൽ സൗകര്യം ലഭ്യമാകുമ്പോൾ.',
  'family.childFallback': 'കുട്ടി',
  'family.childCount_one': '{count} കുട്ടി',
  'family.childCount_other': '{count} കുട്ടികൾ',

  // ── Fees ──────────────────────────────────────────────────────────────────

  // ── Attendance ────────────────────────────────────────────────────────────

  // ── Shared states ─────────────────────────────────────────────────────────

  // ── Auth ──────────────────────────────────────────────────────────────────
};
